import { zodResolver } from '@hookform/resolvers/zod';
import {
  Calendar,
  Download,
  Edit,
  FileSpreadsheet,
  FileText,
  MoreHorizontal,
  Plus,
  Trash,
} from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useGetAllClassesWithSearch } from '~entities/classes/api/get-all-classes-with-search';
import { useCreateShowMutation } from '~entities/recital-manager/api/create-show';
import { useDeleteShowMutation } from '~entities/recital-manager/api/delete-show';
import { useDetectConflictsMutation } from '~entities/recital-manager/api/detect-conflicts';
import { downloadPerformanceLineup } from '~entities/recital-manager/api/export-performance-lineup';
import { downloadPerformanceSchedule } from '~entities/recital-manager/api/export-performance-schedule';
import { useGetAllShows } from '~entities/recital-manager/api/get-all-shows';
import { useImportClassesMutation } from '~entities/recital-manager/api/import-classes';
import { useUpdateShowMutation } from '~entities/recital-manager/api/update-show';
import type { Show } from '~entities/recital-manager/model/recital';
import { ClassImportDialog } from '~entities/recital-manager/ui/class-import-dialog';
import { useCurrentStudio } from '~entities/studios/model/studio-store';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/shadcn/ui/alert-dialog';
import { Button } from '~/shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/shared/shadcn/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/shared/shadcn/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~/shared/shadcn/ui/dropdown-menu';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/shared/shadcn/ui/form';
import { Input } from '~/shared/shadcn/ui/input';
import { Textarea } from '~/shared/shadcn/ui/textarea';
import {
  convertLocalToUTC,
  convertUTCToLocal,
  formatRecitalDate,
} from '~shared/utils/recital-timezone';

import { ActsList } from './acts-list';
import { ShowSwitcher } from './show-switcher';
import { useShowsWithPerformances } from './use-shows-with-performances';

interface ShowsTabProps {
  recitalId: string;
}

const showFormSchema = z.object({
  name: z.string().min(1, 'Show name is required'),
  showDate: z.string().min(1, 'Show date is required'),
  capacity: z.coerce.number().optional(),
  notes: z.string().optional(),
});

type ShowFormValues = z.infer<typeof showFormSchema>;

export const ShowsTab = ({ recitalId }: ShowsTabProps) => {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedShow, setSelectedShow] = useState<Show | null>(null);
  const [activeShowId, setActiveShowId] = useState<string>('');
  const [classSearchQuery, setClassSearchQuery] = useState('');
  const [classPage, setClassPage] = useState(1);
  const classLimit = 50;

  // Get studio timezone
  const studio = useCurrentStudio();
  const timezone = studio?.timezone ?? 'UTC';

  // Get user role for access control
  const { hasAdminAccess } = useCurrentUserRole();

  const { data: showsData, isLoading } = useGetAllShows({ recitalId });

  const { showsWithPerformances, loading: showsWithPerformancesLoading } =
    useShowsWithPerformances(showsData?.data ?? []);

  const { data: classesData, isLoading: isLoadingClasses } =
    useGetAllClassesWithSearch({
      page: classPage,
      limit: classLimit,
      name: classSearchQuery || undefined,
    });
  const createShowMutation = useCreateShowMutation();
  const updateShowMutation = useUpdateShowMutation();
  const deleteShowMutation = useDeleteShowMutation();
  const importClassesMutation = useImportClassesMutation();
  const detectConflictsMutation = useDetectConflictsMutation();

  // Set the first show as active when data loads
  if (
    !activeShowId &&
    showsData?.data &&
    showsData.data.length > 0 &&
    showsData.data[0]
  ) {
    setActiveShowId(showsData.data[0]._id);
  }

  const form = useForm<ShowFormValues>({
    resolver: zodResolver(showFormSchema),
    defaultValues: {
      name: '',
      showDate: '',
      capacity: undefined,
      notes: '',
    },
  });

  const resetForm = () => {
    form.reset({
      name: '',
      showDate: '',
      capacity: undefined,
      notes: '',
    });
  };

  const handleCreate = (values: ShowFormValues) => {
    // Convert datetime-local value to UTC for backend storage
    const utcDate = convertLocalToUTC(values.showDate, timezone);

    createShowMutation.mutate(
      {
        recitalId,
        ...values,
        showDate: utcDate.toISOString(),
        orderInRecital: (showsData?.data.length ?? 0) + 1,
      },
      {
        onSuccess: () => {
          toast.success('Show created successfully!');
          setCreateDialogOpen(false);
          resetForm();
        },
        onError: (error: any) => {
          const errorMessage =
            error.response?.data?.error?.message ||
            error.response?.data?.message ||
            'Failed to create show';
          toast.error(errorMessage);
        },
      },
    );
  };

  const handleEdit = (values: ShowFormValues) => {
    if (!selectedShow) return;

    // Convert datetime-local value to UTC for backend storage
    const utcDate = convertLocalToUTC(values.showDate, timezone);

    updateShowMutation.mutate(
      {
        recitalId,
        showId: selectedShow._id,
        ...values,
        showDate: utcDate.toISOString(),
      },
      {
        onSuccess: () => {
          toast.success('Show updated successfully!');
          setEditDialogOpen(false);
          setSelectedShow(null);
          resetForm();
        },
        onError: (error: any) => {
          const errorMessage =
            error.response?.data?.error?.message ||
            error.response?.data?.message ||
            'Failed to update show';
          toast.error(errorMessage);
        },
      },
    );
  };

  const handleDelete = () => {
    if (!selectedShow) return;

    deleteShowMutation.mutate(
      { recitalId, showId: selectedShow._id },
      {
        onSuccess: () => {
          toast.success('Show deleted successfully!');
          setDeleteDialogOpen(false);
          setSelectedShow(null);
        },
        onError: (error: any) => {
          const errorMessage =
            error.response?.data?.error?.message ||
            error.response?.data?.message ||
            'Failed to delete show';
          toast.error(errorMessage);
        },
      },
    );
  };

  const openEditDialog = (show: Show) => {
    setSelectedShow(show);
    // Convert UTC date to studio timezone for datetime-local input
    const formattedDate = convertUTCToLocal(show.showDate, timezone);
    form.reset({
      name: show.name,
      showDate: formattedDate,
      capacity: show.capacity,
      notes: show.notes,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (show: Show) => {
    setSelectedShow(show);
    setDeleteDialogOpen(true);
  };

  const handleImportClasses = async (
    classIds: Array<string>,
    showId: string,
    defaultDuration?: number,
    actId?: string,
    selectedStudentIds?: Array<string>,
  ) => {
    try {
      const result = await importClassesMutation.mutateAsync({
        recitalId,
        data: {
          classIds,
          showId,
          defaultDuration,
          actId,
          selectedStudentIds,
        },
      });

      toast.success(`Imported ${result.imported} class(es) as performances`);

      // Auto-detect conflicts after import
      try {
        const conflictsResult = await detectConflictsMutation.mutateAsync({
          recitalId,
          data: { forceRecheck: true },
        });

        if (conflictsResult.conflictsFound > 0) {
          toast.warning(
            `Found ${conflictsResult.conflictsFound} conflict(s) - check the Conflicts tab`,
          );
        }
      } catch (conflictError) {
        console.error('Error detecting conflicts:', conflictError);
        // Don't show error toast for conflict detection failure
      }
    } catch (error) {
      throw error; // Re-throw to let ClassImportDialog handle it
    }
  };

  const handleExportLineup = async (showId: string, format: 'csv' | 'pdf') => {
    try {
      await downloadPerformanceLineup(showId, format);
      toast.success(`Performance lineup exported as ${format.toUpperCase()}`);
    } catch (error: unknown) {
      console.error('Failed to export performance lineup:', error);
      const errorMessage =
        error instanceof Error && error.message.trim().length > 0
          ? error.message
          : 'Failed to export performance lineup';
      toast.error(errorMessage);
    }
  };

  const handlePrintSchedule = async () => {
    try {
      await downloadPerformanceSchedule(recitalId, 'pdf');
      toast.success('Performance schedule downloaded as PDF');
    } catch (error) {
      console.error('Failed to download performance schedule:', error);
      toast.error('Failed to download performance schedule');
    }
  };

  const activeShow = showsData?.data.find((show) => show._id === activeShowId);

  return (
    <div>
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle>Shows & Performances</CardTitle>
              <CardDescription>
                Build your recital by organizing performances into shows
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading || showsWithPerformancesLoading ? (
            <div className='py-8 text-center'>
              Loading shows and performances...
            </div>
          ) : !showsData?.data || showsData.data.length === 0 ? (
            <div className='rounded-lg border-2 border-dashed py-12 text-center'>
              <Calendar className='mx-auto mb-4 size-12 text-muted-foreground' />
              <h3 className='mb-2 text-lg font-semibold'>
                No shows created yet
              </h3>
              <p className='mb-4 text-muted-foreground'>
                Get started by creating your first show
              </p>
              <Button
                onClick={() => {
                  setCreateDialogOpen(true);
                }}
              >
                <Plus />
                Create First Show
              </Button>
            </div>
          ) : (
            <div className='space-y-6'>
              {/* Show Switcher */}
              <ShowSwitcher
                shows={showsWithPerformances}
                selectedShowId={activeShowId}
                onShowSelect={setActiveShowId}
                onAddShow={() => {
                  setCreateDialogOpen(true);
                }}
                onPrintSchedule={handlePrintSchedule}
                timezone={timezone}
              />

              {/* Active Show Content */}
              {activeShow && (
                <div className='space-y-4'>
                  <div className='flex flex-col gap-4 rounded-lg bg-muted/50 p-4 sm:flex-row sm:items-start sm:justify-between'>
                    <div className='min-w-0 flex-1'>
                      <h4 className='text-lg font-semibold capitalize break-words'>
                        {activeShow.name}
                      </h4>
                      <p className='text-sm text-muted-foreground break-words'>
                        {activeShow.showDate
                          ? formatRecitalDate(
                              activeShow.showDate,
                              timezone,
                              'EEEE, MM/dd/yyyy h:mm a',
                            )
                          : 'Invalid date'}
                      </p>
                      {activeShow.notes ? (
                        <p className='mt-1 text-sm text-muted-foreground break-words'>
                          {activeShow.notes}
                        </p>
                      ) : null}
                    </div>
                    <div className='flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row'>
                      <Button
                        variant='outline'
                        size='sm'
                        className='w-full sm:w-auto'
                        onClick={() => {
                          setImportDialogOpen(true);
                        }}
                      >
                        <Download className='mr-2 size-4 shrink-0' />
                        Import from Classes
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant='outline'
                            size='sm'
                            className='w-full sm:w-auto'
                          >
                            <MoreHorizontal className='mr-2 size-4 sm:mr-0' />
                            <span className='sm:sr-only'>Show actions</span>
                            <span className='sm:hidden'>More actions</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                          <DropdownMenuItem
                            onClick={() =>
                              handleExportLineup(activeShow._id, 'csv')
                            }
                          >
                            <FileSpreadsheet className='mr-2 size-4' />
                            Export Lineup (CSV)
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              handleExportLineup(activeShow._id, 'pdf')
                            }
                          >
                            <FileText className='mr-2 size-4' />
                            Export Lineup (PDF)
                          </DropdownMenuItem>
                          {hasAdminAccess && (
                            <>
                              <DropdownMenuItem
                                onClick={() => {
                                  openEditDialog(activeShow);
                                }}
                              >
                                <Edit className='mr-2 size-4' />
                                Edit Show
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  openDeleteDialog(activeShow);
                                }}
                                className='text-red-600'
                              >
                                <Trash className='mr-2 size-4' />
                                Delete Show
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Acts & Performances List */}
                  <ActsList showId={activeShowId} recitalId={recitalId} />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Show Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Show</DialogTitle>
            <DialogDescription>
              Add a new show to this recital
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleCreate)}
              className='space-y-4'
            >
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Show Name *</FormLabel>
                    <FormControl>
                      <Input placeholder='Saturday Evening Show' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='showDate'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Show Date & Time *</FormLabel>
                    <FormControl>
                      <Input type='datetime-local' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='capacity'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Capacity</FormLabel>
                    <FormControl>
                      <Input
                        type='number'
                        placeholder='Maximum number of performances'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='notes'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder='Any special notes for this show...'
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => {
                    setCreateDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type='submit' disabled={createShowMutation.isPending}>
                  {createShowMutation.isPending ? 'Creating...' : 'Create Show'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Show Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Show</DialogTitle>
            <DialogDescription>Update show details</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleEdit)}
              className='space-y-4'
            >
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Show Name *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='showDate'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Show Date & Time *</FormLabel>
                    <FormControl>
                      <Input type='datetime-local' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='capacity'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Capacity</FormLabel>
                    <FormControl>
                      <Input type='number' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='notes'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type='button'
                  variant='outline'
                  onClick={() => {
                    setEditDialogOpen(false);
                    setSelectedShow(null);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type='submit' disabled={updateShowMutation.isPending}>
                  {updateShowMutation.isPending ? 'Updating...' : 'Update Show'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Show</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedShow?.name}&quot;?
              This will also delete all performances associated with this show.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className='bg-red-600 hover:bg-red-700'
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Class Import Dialog */}
      {activeShowId && (
        <ClassImportDialog
          isOpen={importDialogOpen}
          onClose={() => {
            setImportDialogOpen(false);
            setClassSearchQuery('');
            setClassPage(1);
          }}
          recitalId={recitalId}
          showId={activeShowId}
          classes={
            classesData?.data.classes.map((cls) => ({
              _id: cls._id,
              name: cls.title,
              location: cls.location.name,
              dayOfWeek: cls.days.join(', '),
              startTime: cls.startTime,
              instructor: cls.instructor[0]?.name,
              students:
                (cls.students as Array<{
                  _id: string;
                  firstName: string;
                  lastName: string;
                }>) || [],
            })) ?? []
          }
          isLoadingClasses={isLoadingClasses}
          onImport={handleImportClasses}
          searchQuery={classSearchQuery}
          onSearchChange={setClassSearchQuery}
          pagination={classesData?.pagination}
          onLoadMore={() => {
            setClassPage(classPage + 1);
          }}
        />
      )}
    </div>
  );
};
